import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Card } from "./ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";
import { toast } from "sonner";
import { Beaker, Lock, Plus, Trash2 } from "lucide-react";
import { Link } from "react-router-dom";
import { useI18n } from "../lib/LanguageContext";
import { localizeContent } from "../lib/translations";

const PRESETS = [
  { label: "Omega-3 (EPA+DHA)", unit: "mg", daily_target: 250, group: "custom" },
  { label: "Kolin", unit: "mg", daily_target: 550, group: "custom" },
  { label: "Isoflavon Kedelai", unit: "mg", daily_target: 50, group: "custom" },
  { label: "Karotenoid Total", unit: "µg", daily_target: 6000, group: "custom" },
  { label: "Kreatin", unit: "g", daily_target: 3, group: "custom" },
  { label: "Kafein", unit: "mg", daily_target: 400, group: "custom" },
];

export default function CustomNutrientsCard({ values, onValuesChange }) {
  const { lang } = useI18n();
  const tr = (value) => localizeContent(value, lang);
  const { user } = useAuth();
  const [defs, setDefs] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ label: "", unit: "mg", daily_target: 0, group: "custom" });

  const pro = user?.tier === "professional";

  useEffect(() => { if (pro) api.get("/custom-nutrients").then(r => setDefs(r.data)).catch(()=>{}); }, [pro]);

  const create = async (payload) => {
    try {
      const r = await api.post("/custom-nutrients", payload);
      setDefs(prev => [r.data, ...prev]); toast.success(`${tr(payload.label)} ${tr("ditambahkan")}`);
      setForm({ label: "", unit: "mg", daily_target: 0, group: "custom" });
    } catch (err) { toast.error(err.response?.data?.detail || tr("Gagal")); }
  };

  const remove = async (id, key) => {
    if (!window.confirm(tr("Hapus nutrient custom ini?"))) return;
    try {
      await api.delete(`/custom-nutrients/${id}`);
      setDefs(prev => prev.filter(d => d.id !== id));
      const nv = { ...values }; delete nv[key]; onValuesChange(nv);
      toast.success(tr("Terhapus"));
    } catch { toast.error(tr("Gagal menghapus")); }
  };

  const setValue = (key, val) => onValuesChange({ ...values, [key]: Number(val) || 0 });

  if (!pro) return (
    <Card className="dash-panel p-5 border-dashed" data-testid="custom-nutrient-locked">
      <div className="flex items-start gap-3">
        <div className="p-2 bg-amber-50 rounded-md"><Lock className="w-4 h-4 text-amber-600"/></div>
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1"><Beaker className="w-4 h-4 text-teal"/><h3 className="font-display font-medium">{tr("Custom Nutrient Panel")}</h3><span className="text-[10px] tracking-widest uppercase text-amber-600 font-bold bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5">{tr("Add-on")}</span></div>
          <p className="text-xs text-muted-foreground mb-3">{tr("Tambahkan zat gizi khusus (omega-3, kolin, isoflavon, karotenoid, dll) di luar 32 default. Cocok untuk klinik dan penelitian.")}</p>
          <Link to="/harga"><Button size="sm" className="rounded-full bg-teal hover:bg-teal/90 text-white" data-testid="upgrade-custom-nutrient-btn">{tr("Upgrade ke Professional · Rp 299.000/bulan")}</Button></Link>
        </div>
      </div>
    </Card>
  );

  return (
    <Card className="dash-panel p-5" data-testid="custom-nutrient-panel">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2"><Beaker className="w-4 h-4 text-teal"/><h3 className="font-display font-medium">{tr("Custom Nutrient")}</h3></div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button size="sm" variant="outline" className="rounded-md" data-testid="manage-custom-btn"><Plus className="w-3.5 h-3.5 mr-1"/> {tr("Kelola")}</Button></DialogTrigger>
          <DialogContent className="bg-white max-w-2xl">
            <DialogHeader><DialogTitle className="font-display">{tr("Kelola Custom Nutrient")}</DialogTitle></DialogHeader>
            <div className="grid md:grid-cols-2 gap-6 mt-2">
              <div>
                <div className="text-xs uppercase tracking-widest text-teal font-bold mb-2">{tr("Preset Umum")}</div>
                <div className="space-y-1.5">
                  {PRESETS.map(p => (
                    <button key={p.label} onClick={()=>create(p)} className="w-full text-left border border-teal/15 rounded-md px-3 py-2 hover:bg-teal-soft/40 text-sm flex items-center justify-between" data-testid={`preset-${p.label}`}>
                      <span>{tr(p.label)}</span><span className="text-xs text-muted-foreground">{p.daily_target} {p.unit}{tr("/hari")}</span>
                    </button>
                  ))}
                </div>
                <div className="mt-4 border-t border-teal/10 pt-4">
                  <div className="text-xs uppercase tracking-widest text-teal font-bold mb-2">{tr("Buat Sendiri")}</div>
                  <div className="space-y-2">
                    <div><Label className="text-xs">{tr("Nama")}</Label><Input value={form.label} onChange={(e)=>setForm({...form, label: e.target.value})} placeholder={tr("cth: Kurkumin")} data-testid="custom-name"/></div>
                    <div className="grid grid-cols-2 gap-2">
                      <div><Label className="text-xs">{tr("Unit")}</Label>
                        <select value={form.unit} onChange={(e)=>setForm({...form, unit: e.target.value})} className="w-full border border-input rounded-md h-10 px-2 text-sm bg-white">
                          <option value="mg">mg</option><option value="µg">µg</option><option value="g">g</option><option value="IU">IU</option>
                        </select>
                      </div>
                      <div><Label className="text-xs">{tr("Target/hari")}</Label><Input type="number" value={form.daily_target} onChange={(e)=>setForm({...form, daily_target: +e.target.value})} data-testid="custom-target"/></div>
                    </div>
                    <Button size="sm" className="w-full rounded-md bg-teal hover:bg-teal/90 text-white" onClick={()=>form.label && create(form)} data-testid="add-custom-btn">{tr("Tambahkan")}</Button>
                  </div>
                </div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-teal font-bold mb-2">{tr("Terdaftar")} ({defs.length})</div>
                <div className="space-y-1.5 max-h-[420px] overflow-y-auto">
                  {defs.length === 0 ? <p className="text-sm text-muted-foreground">{tr("Belum ada. Klik preset atau buat sendiri.")}</p> :
                    defs.map(d => (
                      <div key={d.id} className="border border-teal/10 rounded-md p-2.5 flex items-center justify-between" data-testid={`cn-def-${d.id}`}>
                        <div className="min-w-0">
                          <div className="text-sm font-medium truncate">{tr(d.label)}</div>
                          <div className="text-xs text-muted-foreground">{tr("Target:")} {d.daily_target} {d.unit}{tr("/hari")}</div>
                        </div>
                        <button onClick={()=>remove(d.id, d.key)} className="text-destructive p-1"><Trash2 className="w-4 h-4"/></button>
                      </div>
                    ))
                  }
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {defs.length === 0 ? <p className="text-xs text-muted-foreground">{tr('Klik "Kelola" untuk menambahkan nutrisi custom (omega-3, kolin, dll).')}</p> :
        <div className="space-y-2">
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground">{tr("Masukkan total asupan hari ini")}</p>
          {defs.map(d => (
            <div key={d.id} className="flex items-center gap-2" data-testid={`cn-input-${d.key}`}>
              <div className="flex-1 min-w-0">
                <div className="text-xs font-medium truncate">{tr(d.label)}</div>
                <div className="text-[10px] text-muted-foreground">{tr("Target:")} {d.daily_target} {d.unit}</div>
              </div>
              <Input type="number" className="w-24 h-9" value={values?.[d.key] ?? ""} onChange={(e)=>setValue(d.key, e.target.value)}/>
              <span className="text-xs w-6">{d.unit}</span>
            </div>
          ))}
        </div>
      }
    </Card>
  );
}
